import { useEffect, useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Minus, PackageCheck, Plus } from "lucide-react";
import {
  INVENTORY_CATEGORY_LABELS,
  INVENTORY_TEMPLATES,
  type TemplateInventoryItem,
} from "@/data/inventoryTemplates";

export type InventorySelection = { name: string; checked: boolean; defaultQty: number };

interface Step5InventoryProps {
  selectionsJson: string;
  onSelectionsChange: (json: string) => void;
  onModeChange?: (mode: "recommended" | "manual" | null) => void;
}

function defaultSelections(): InventorySelection[] {
  return INVENTORY_TEMPLATES.map((item) => ({
    name: item.name,
    checked: true,
    defaultQty: item.defaultQty,
  }));
}

function parseSelections(json: string): InventorySelection[] {
  if (!json) return defaultSelections();
  try {
    const parsed = JSON.parse(json) as InventorySelection[];
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    /* ignore */
  }
  return defaultSelections();
}

function ChoiceCard({
  title,
  description,
  icon: Icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: typeof PackageCheck;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-start gap-3 p-5 rounded-xl border-2 border-border text-left",
        "hover:border-primary/40 hover:bg-muted/40 transition-all"
      )}
    >
      <Icon className="h-6 w-6 text-primary" />
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>
    </button>
  );
}

export function Step5Inventory({
  selectionsJson,
  onSelectionsChange,
  onModeChange,
}: Step5InventoryProps) {
  const [mode, setMode] = useState<"recommended" | "manual" | null>(null);

  const setModeAndNotify = (next: "recommended" | "manual" | null) => {
    setMode(next);
    onModeChange?.(next);
  };

  const selections = useMemo(() => parseSelections(selectionsJson), [selectionsJson]);

  const byCategory = useMemo(() => {
    const map = new Map<TemplateInventoryItem["category"], InventorySelection[]>();
    for (const item of INVENTORY_TEMPLATES) {
      const sel = selections.find((s) => s.name === item.name);
      const entry = sel ?? { name: item.name, checked: true, defaultQty: item.defaultQty };
      if (!map.has(item.category)) map.set(item.category, []);
      map.get(item.category)!.push(entry);
    }
    return map;
  }, [selections]);

  const updateSelections = (next: InventorySelection[]) => {
    onSelectionsChange(JSON.stringify(next));
  };

  const toggle = (name: string, checked: boolean) => {
    updateSelections(selections.map((s) => (s.name === name ? { ...s, checked } : s)));
  };

  const updateQty = (name: string, delta: number) => {
    updateSelections(
      selections.map((s) => {
        if (s.name !== name) return s;
        return { ...s, defaultQty: Math.max(0, s.defaultQty + delta) };
      })
    );
  };

  const setQty = (name: string, value: number) => {
    updateSelections(
      selections.map((s) => {
        if (s.name !== name) return s;
        return { ...s, defaultQty: Math.max(0, value) };
      })
    );
  };

  useEffect(() => {
    if (mode === "recommended" && !selectionsJson) {
      onSelectionsChange(JSON.stringify(defaultSelections()));
    }
  }, [mode, selectionsJson, onSelectionsChange]);

  if (mode === null) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Inventory</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track supplies for turnovers. Choose how you want to get started.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ChoiceCard
            title="Recommended items"
            description="We'll pre-fill common supplies. Adjust counts or remove what you don't stock."
            icon={PackageCheck}
            onClick={() => setModeAndNotify("recommended")}
          />
          <ChoiceCard
            title="Start from scratch"
            description="Skip pre-fills. Add your own items from the property page after setup."
            icon={Plus}
            onClick={() => setModeAndNotify("manual")}
          />
        </div>
      </div>
    );
  }

  if (mode === "manual") {
    return (
      <div className="space-y-6">
        <div>
          <Button
            type="button"
            variant="link"
            className="h-auto p-0 text-muted-foreground"
            onClick={() => setModeAndNotify(null)}
          >
            ← Change
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Start from scratch</h1>
          <p className="text-muted-foreground text-sm mt-3">
            No inventory added. You can add items from the Inventory tab on the property page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-muted-foreground"
          onClick={() => setModeAndNotify(null)}
        >
          ← Change
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight mt-2">Recommended inventory</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Common supplies to track per turnover. Uncheck items you do not stock.
        </p>
      </div>

      {[...byCategory.entries()].map(([category, items]) => (
        <div key={category} className="rounded-lg border overflow-hidden">
          <div className="px-4 py-2 bg-muted/50 border-b text-sm font-medium">
            {INVENTORY_CATEGORY_LABELS[category]}
          </div>
          <ul className="divide-y">
            {items.map((item) => {
              const meta = INVENTORY_TEMPLATES.find((t) => t.name === item.name)!;
              return (
                <li key={item.name} className="flex items-center gap-2 px-4 py-3">
                  <Checkbox
                    id={`inv-${item.name}`}
                    checked={item.checked}
                    onCheckedChange={(v) => toggle(item.name, v === true)}
                  />
                  <label
                    htmlFor={`inv-${item.name}`}
                    className="flex-1 text-sm cursor-pointer min-w-0"
                  >
                    {item.name}
                    <span className="text-muted-foreground ml-1">({meta.unit})</span>
                  </label>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!item.checked || item.defaultQty <= 0}
                      onClick={() => updateQty(item.name, -1)}
                      aria-label={`Decrease ${item.name}`}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <Input
                      type="number"
                      min={0}
                      className="h-7 w-14 text-center text-sm px-1"
                      value={item.defaultQty}
                      disabled={!item.checked}
                      onChange={(e) => setQty(item.name, parseInt(e.target.value, 10) || 0)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      disabled={!item.checked}
                      onClick={() => updateQty(item.name, 1)}
                      aria-label={`Increase ${item.name}`}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
